import React, { useEffect, useState } from "react";
import { Button, Typography, CircularProgress } from "@material-ui/core";
import { toast } from "react-toastify";
import useSettings from "../../hooks/useSettings";
import LoginTheme, { LOGIN_THEMES } from "./index";
import "./themeManager.css";

const ThemeManager = ({ externalPreview = false, onSelectionChange, onActiveChange }) => {
  const { getPublicLoginTheme, update } = useSettings();
  const [active, setActive] = useState("classic");
  const [selection, setSelection] = useState("classic");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    getPublicLoginTheme()
      .then((theme) => {
        if (mounted) {
          const id = LOGIN_THEMES.some((item) => item.id === String(theme)) ? String(theme) : "classic";
          setSelection(id);
          setActive(id);
          if (onSelectionChange) onSelectionChange(id);
          if (onActiveChange) onActiveChange(id);
        }
      })
      .catch(() => { if (mounted) toast.error("Não foi possível carregar o tema de login."); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const chooseTheme = (id) => {
    setSelection(id);
    if (onSelectionChange) onSelectionChange(id);
  };

  const save = async () => {
    if (saving || selection === active) return;
    setSaving(true);
    try {
      await update({ key: "loginPageTheme", value: selection });
      setActive(selection);
      if (onActiveChange) onActiveChange(selection);
      toast.success("Página de login atualizada. O novo tema já está disponível para os próximos acessos.");
    } catch (_) {
      toast.error("Não foi possível salvar o tema da página de login.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`theme-manager ${externalPreview ? "tm-embedded" : ""}`}>
      <div className="theme-manager-heading">
        <div>
          <Typography variant="h6">Página de Login</Typography>
          <Typography variant="body2" color="textSecondary">
            Escolha um dos 10 modelos. A seleção só será publicada quando clicar em Salvar.
          </Typography>
        </div>
        <div className="tm-active-status">Tema publicado: {active === "classic" ? "Original" : `Modelo ${active}`}</div>
      </div>
      {loading ? <div className="tm-loading"><CircularProgress size={25} /> Carregando modelos...</div> : <>
        <div className="tm-gallery">
          {LOGIN_THEMES.map((theme) => (
            <button key={theme.id} type="button" className={`tm-theme ${selection === theme.id ? "tm-selected" : ""}`} onClick={() => chooseTheme(theme.id)} aria-pressed={selection === theme.id}>
              <span className={`tm-mini tm-mini-${theme.id}`}>
                <span className="tm-mini-hero"><i className="tm-mini-logo"/><span/><span/><span/></span>
                <span className="tm-mini-card"><i/><b/><b/><em/></span>
              </span>
              <span className="tm-theme-info"><strong>{theme.id}. {theme.name}</strong><small>{theme.detail}</small></span>
              {active === theme.id && <span className="tm-published">Publicado</span>}
              {selection === theme.id && <span className="tm-chosen">✓</span>}
            </button>
          ))}
        </div>
        <div className="tm-selection-actions">
          <Button size="small" variant="outlined" onClick={() => chooseTheme("classic")} disabled={saving}>Manter login original</Button>
          <div className="tm-buttons">
            <Button variant="outlined" onClick={() => setPreviewOpen(true)} disabled={selection === "classic"}>Visualizar página</Button>
            <Button color="primary" variant="contained" disabled={saving || selection === active} onClick={save}>{saving ? "Salvando..." : "Salvar tema de login"}</Button>
          </div>
        </div>
        {!externalPreview && selection !== "classic" && <div className="tm-inline-preview">
          <div className="tm-preview-title"><strong>Prévia do modelo {selection}</strong><span>Mesma estrutura utilizada na página real</span></div>
          <LoginTheme themeId={selection} preview />
        </div>}
        <p className="tm-note">O tema se aplica à página pública de acesso do sistema. Não altera usuários, senhas, autenticação, permissões ou logos cadastradas.</p>
      </>}
      {previewOpen && selection !== "classic" && <div className="tm-overlay" role="dialog" aria-modal="true" aria-label="Prévia da página de login">
        <div className="tm-overlay-body">
          <div className="tm-overlay-header"><strong>Prévia — Modelo {selection}</strong><Button onClick={() => setPreviewOpen(false)}>Fechar</Button></div>
          <div className="tm-overlay-content"><LoginTheme themeId={selection} preview /></div>
        </div>
      </div>}
    </div>
  );
};

export default ThemeManager;
